import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Description as DocIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ReactSelect, { components } from 'react-select';
import orgDocumentService from '../services/orgDocumentService';
import { PageTitleContainer } from '../components/common';

const DOCUMENT_TYPES = [
  { value: 'FIELD_VALIDATION', label: 'Field Validation' },
  { value: 'DEV_GUIDELINES', label: 'Dev Guidelines' },
  { value: 'DESIGN_GUIDELINES', label: 'Design Guidelines' },
  { value: 'SECURITY_GUIDELINES', label: 'Security Guidelines' },
  { value: 'API_GUIDELINES', label: 'API Guidelines' },
  { value: 'CODING_STANDARDS', label: 'Coding Standards' },
];

const OrgDocuments = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('');

  // Upload dialog state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [formData, setFormData] = useState({
    document_type: 'FIELD_VALIDATION',
    title: '',
    description: '',
    version: '1.0',
    file: null,
  });
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Refs and dynamic height for scrollable content area
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState('calc(100vh - 130px)');

  useLayoutEffect(() => {
    const updateContentHeight = () => {
      const mainHeaderHeight = 73;
      const titleHeaderHeight = 80;
      const extraPadding = 69;
      const totalHeaderHeight = mainHeaderHeight + titleHeaderHeight + extraPadding;
      setContentHeight(`calc(100vh - ${totalHeaderHeight}px)`);
    };
    updateContentHeight();
    window.addEventListener('resize', updateContentHeight);
    return () => window.removeEventListener('resize', updateContentHeight);
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [filterType]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const result = await orgDocumentService.getAll(filterType || null);
      setDocuments(result.data || []);
      setError(null);
    } catch (err) {
      setError('Failed to load organization documents');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenUpload = (doc = null) => {
    if (doc) {
      setEditDoc(doc);
      setFormData({
        document_type: doc.document_type,
        title: doc.title,
        description: doc.description || '',
        version: doc.version || '1.0',
        file: null,
      });
    } else {
      setEditDoc(null);
      setFormData({
        document_type: 'FIELD_VALIDATION',
        title: '',
        description: '',
        version: '1.0',
        file: null,
      });
    }
    setUploadOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (editDoc) {
        const fd = new FormData();
        if (formData.title) fd.append('title', formData.title);
        if (formData.description) fd.append('description', formData.description);
        if (formData.version) fd.append('version', formData.version);
        await orgDocumentService.update(editDoc.id, fd);
      } else {
        const fd = new FormData();
        fd.append('document_type', formData.document_type);
        fd.append('title', formData.title);
        fd.append('description', formData.description);
        fd.append('version', formData.version);
        if (formData.file) {
          fd.append('file', formData.file);
        }
        await orgDocumentService.create(fd);
      }
      setUploadOpen(false);
      loadDocuments();
    } catch (err) {
      setError('Failed to save document');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await orgDocumentService.delete(deleteTarget.id);
      setDeleteTarget(null);
      loadDocuments();
    } catch (err) {
      setError('Failed to delete document');
      console.error(err);
    }
  };

  const handleToggleActive = async (doc) => {
    try {
      const fd = new FormData();
      fd.append('is_active', (!doc.is_active).toString());
      await orgDocumentService.update(doc.id, fd);
      loadDocuments();
    } catch (err) {
      setError('Failed to update document status');
      console.error(err);
    }
  };

  const getTypeLabel = (value) => {
    const found = DOCUMENT_TYPES.find((t) => t.value === value);
    return found ? found.label : value;
  };

  return (<Box
    className="CreateProject-root"
    sx={{
      width: '100%',
      bgcolor: '#f5f6f7',
      minHeight: '100vh',
      pb: 4,
      px: 6,
      pt: 4,
      overflowX: 'hidden',
      backgroundImage: 'none', // Explicitly remove any background image
      '&::before': { display: 'none' },
      '&::after': { display: 'none' }
    }}>
    {/* Header */}
    <PageTitleContainer
      title={
        <Box sx={{ display: 'flex !important', alignItems: 'center' }}>

          <Box>
            <Typography
              variant="h5"
              component="span"
              sx={{ fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700, display: 'flex', alignItems: "center" }}
            >
              <Box>
                <IconButton
                  onClick={() => navigate('/settings')}
                  size="small"
                  sx={{ mr: 1, cursor: 'pointer', '& svg': { cursor: 'pointer' } }}
                >
                  <ArrowBackIcon />
                </IconButton>
              </Box>
              Organization Documents
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage Infanion default documents used across all projects
            </Typography>
          </Box>
        </Box>
      }
      justifyContent={'space-between'}
      rightContent={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenUpload()}
          sx={{ whiteSpace: "nowrap", textTransform: 'none !important', bgcolor: '#485869', '&:hover': { bgcolor: '#3a4a59' } }}
        >
          Upload document
        </Button>
      }
      sx={{
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        ml: 0,
        mr: 0,
        mb: 2,
        mt: 0,
        py: '16px' // Exact 16px padding top and bottom
      }}
      titleProps={{ component: 'div', sx: { width: '100%' } }}
    />

    {error && (
      <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
        {error}
      </Alert>
    )}
    <Paper
      elevation={0}
      ref={contentRef}
      sx={{
        borderRadius: '4px',
        p: 5,
        width: '100%',
        height: contentHeight,
        bgcolor: 'white',
        overflowX: 'hidden',
        overflowY: 'auto',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        backgroundImage: 'none',
        '&::before': {
          display: 'none'
        },
        '&::after': {
          display: 'none'
        }
      }}
    >

      {/* Filter */}
      <Box sx={{ mb: 2, width: "50%", marginLeft: "auto" }}>
        <ReactSelect
          className="basic-single"
          classNamePrefix="select"
          name="filter-by-type"
          inputId="filter-by-type-select"
          instanceId="filter-by-type-instance"
          value={
            filterType
              ? DOCUMENT_TYPES.find((t) => t.value === filterType)
              : { value: '', label: 'All Types' }
          }
          onChange={(option) => setFilterType(option && option.value ? option.value : '')}
          options={[{ value: '', label: 'All Types' }, ...DOCUMENT_TYPES]}
          getOptionLabel={(option) => option.label}
          getOptionValue={(option) => option.value}
          placeholder="Filter by Type"
          isClearable={false}
          isSearchable={false}
          components={{
            Input: (props) => (
              <components.Input
                {...props}
                autoComplete="off"
                aria-autocomplete="none"
                data-form-type="other"
                data-lpignore="true"
              />
            )
          }}
          styles={{
            control: (baseStyles, state) => ({
              ...baseStyles,
              borderRadius: '4px',
              minHeight: '45px',
              borderColor: state.isFocused ? '#475969' : '#4759694d',
              boxShadow: state.isFocused ? 'none !important' : 'none',
              '&:hover': {
                borderColor: '#475969'
              }
            }),
            placeholder: (baseStyles) => ({
              ...baseStyles,
              color: '#a2abb3',
              opacity: 0.7,
              fontSize: '14px !important'
            }),
            singleValue: (baseStyles) => ({
              ...baseStyles,
              fontSize: '14px !important',
              color: 'var(--color-blue, #475969)'
            }),
            menu: (baseStyles) => ({
              ...baseStyles,
              zIndex: 9999
            }),
            menuPortal: (baseStyles) => ({
              ...baseStyles,
              zIndex: 9999
            })
          }}
          menuPortalTarget={document.body}
        />
      </Box>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : documents.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <DocIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No organization documents found</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{
                  fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '20%'
                }}>Title</TableCell>
                <TableCell sx={{
                  fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '15%'
                }}>Type</TableCell>
                <TableCell sx={{
                  fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '8%'
                }}>Version</TableCell>
                <TableCell sx={{
                  fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '25%'
                }}>File</TableCell>
                <TableCell sx={{
                  fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '10%'
                }}>Active</TableCell>
                <TableCell sx={{
                  fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '10%'
                }}>Created</TableCell>
                <TableCell sx={{
                  fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '25%'
                }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((doc) => (
                <TableRow key={doc.id}
                  sx={{
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
                    '&:nth-of-type(odd)': { backgroundColor: 'white' },
                    '&:nth-of-type(even)': { backgroundColor: '#fafafa' }
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {doc.title}
                    </Typography>
                    {doc.description && (
                      <Typography variant="caption" color="text.secondary">
                        {doc.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={getTypeLabel(doc.document_type)} size="small" />
                  </TableCell>
                  <TableCell>{doc.version}</TableCell>
                  <TableCell>
                    <Typography variant="caption">{doc.original_filename || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={doc.is_active}
                      onChange={() => handleToggleActive(doc)}
                      size="small"
                      sx={{ cursor: 'pointer', '& .MuiSwitch-switchBase': { cursor: 'pointer' } }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {doc.created_at
                        ? new Date(doc.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                        : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenUpload(doc)}
                        sx={{ cursor: 'pointer', '& svg': { cursor: 'pointer' } }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => setDeleteTarget(doc)}
                        color="error"
                        sx={{ cursor: 'pointer', '& svg': { cursor: 'pointer' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Upload / Edit Dialog */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ px: 4, pt: 4 }}>
          {editDoc ? 'Edit document' : 'Upload organization document'}
        </DialogTitle>
        <DialogContent sx={{ px: 4, py: 3 }}>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {!editDoc && (
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
                    fontWeight: 700,
                    fontSize: '14px',
                    color: '#475969'
                  }}
                >
                  Document Type
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={formData.document_type}
                    onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}
                    sx={{
                      borderRadius: '4px',
                      height: '45px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4759694d',
                        border: '1px solid #4759694d',
                        borderRadius: '4px',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                      '& .MuiSelect-select': {
                        fontSize: '14px',
                        color: 'var(--color-blue, #475969)',
                        padding: '12px 14px',
                      },
                    }}
                  >
                    {DOCUMENT_TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
                  fontWeight: 700,
                  fontSize: '14px',
                  color: '#475969'
                }}
              >
                Title <span style={{ color: '#d32f2f' }}>*</span>
              </Typography>
              <TextField
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                fullWidth
                placeholder="Enter document title"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    height: '45px',
                    '& fieldset': {
                      borderColor: '#4759694d',
                      border: '1px solid #4759694d',
                      borderRadius: '4px',
                    },
                    '&:hover fieldset': { borderColor: '#475969', borderWidth: '1px' },
                    '&.Mui-focused fieldset': { borderColor: '#475969', borderWidth: '1px' },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    padding: '12px 14px',
                    color: 'var(--color-blue, #475969)',
                  },
                }}
              />
            </Box>

            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
                  fontWeight: 700,
                  fontSize: '14px',
                  color: '#475969'
                }}
              >
                Description
              </Typography>
              <TextField
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={3}
                placeholder="Add a short description"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    '& fieldset': {
                      borderColor: '#4759694d',
                      border: '1px solid #4759694d',
                      borderRadius: '4px',
                    },
                    '&:hover fieldset': { borderColor: '#475969', borderWidth: '1px' },
                    '&.Mui-focused fieldset': { borderColor: '#475969', borderWidth: '1px' },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    color: 'var(--color-blue, #475969)',
                  },
                }}
              />
            </Box>

            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
                  fontWeight: 700,
                  fontSize: '14px',
                  color: '#475969'
                }}
              >
                Version
              </Typography>
              <TextField
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                fullWidth
                placeholder="1.0"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    height: '45px',
                    '& fieldset': {
                      borderColor: '#4759694d',
                      border: '1px solid #4759694d',
                      borderRadius: '4px',
                    },
                    '&:hover fieldset': { borderColor: '#475969', borderWidth: '1px' },
                    '&.Mui-focused fieldset': { borderColor: '#475969', borderWidth: '1px' },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    padding: '12px 14px',
                    color: 'var(--color-blue, #475969)',
                  },
                }}
              />
            </Box>

            {!editDoc && (
              <Tooltip title={formData.file ? formData.file.name : ''} placement="top" arrow>
                <Button
                  component="label"
                  startIcon={<UploadIcon sx={{ color: '#475969' }} />}
                  sx={{
                    textTransform: 'none',
                    justifyContent: 'center',
                    bgcolor: '#f5f6f7',
                    color: '#475969',
                    fontSize: '14px',
                    py: 1.5,
                    borderRadius: '4px',
                    border: '1px dashed #4759694d',
                    width: '100%',
                    minWidth: 0,
                    '&:hover': { bgcolor: '#eceef0', borderColor: '#475969' },
                    '& .MuiButton-startIcon': { flexShrink: 0 },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      textAlign: 'center',
                    }}
                  >
                    {formData.file
                      ? formData.file.name
                      : 'Choose File (.txt, .md, .docx, .pdf, .xlsx, .csv)'}
                  </Box>
                  <input
                    type="file"
                    hidden
                    accept=".txt,.md,.docx,.pdf,.xlsx,.xls,.xlsm,.xlsb,.csv"
                    onChange={(e) => setFormData({ ...formData, file: e.target.files[0] || null })}
                  />
                </Button>
              </Tooltip>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setUploadOpen(false)} sx={{ textTransform: 'none', color: '#475969' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !formData.title}
            sx={{
              textTransform: 'none',
              bgcolor: '#485869',
              '&:hover': { bgcolor: '#3a4a59' },
            }}
          >
            {saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : editDoc ? 'Save Changes' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteTarget?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" sx={{ textTransform: 'none' }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  </Box>
  );
};

export default OrgDocuments;
